import { useState, type KeyboardEvent } from "react";
import { nextSuggestionIndex } from "../../../ui/forms/suggestionNavigation";
import { insertSuggestion, matchSuggestions, suggestionQueryAt, type Suggestion } from "./suggestions";
import { useSuggestionSources } from "./useSuggestionSources";

interface ComposerSuggestionsInput {
  readonly text: string;
  readonly caret: number;
  readonly projectId: string;
  /** The draft after a pick, and the pick, so the composer can note what was picked. */
  readonly onInsert: (inserted: { readonly text: string; readonly caret: number }, suggestion: Suggestion) => void;
}

export interface ComposerSuggestions {
  /** Empty while no list is open. */
  readonly suggestions: readonly Suggestion[];
  readonly activeIndex: number;
  /** A source could not be read while a query is open. */
  readonly someFailed: boolean;
  readonly choose: (suggestion: Suggestion) => void;
  /** Up and Down move, Enter and Tab pick, Escape closes; `true` when the list used the key. */
  readonly handleKey: (event: KeyboardEvent<HTMLTextAreaElement>) => boolean;
}

/** The composer's suggestion list: what it shows for the word at the caret, and its keys. */
export function useComposerSuggestions({ text, caret, projectId, onInsert }: ComposerSuggestionsInput): ComposerSuggestions {
  const query = suggestionQueryAt(text, caret);
  const sources = useSuggestionSources(query, projectId);
  const [activeIndex, setActiveIndex] = useState(0);
  // Escape closes the list for the trigger at this index, until the caret leaves the word.
  const [dismissedStart, setDismissedStart] = useState<number | null>(null);
  const queryKey = query === null ? null : `${String(query.start)}${query.trigger}${query.query}`;
  const [shownQueryKey, setShownQueryKey] = useState(queryKey);
  if (shownQueryKey !== queryKey) {
    setShownQueryKey(queryKey);
    setActiveIndex(0);
    if (query === null) setDismissedStart(null);
  }

  const open = query !== null && query.start !== dismissedStart;
  const suggestions = open ? matchSuggestions(query, sources) : [];
  const active = Math.min(activeIndex, Math.max(suggestions.length - 1, 0));

  const choose = (suggestion: Suggestion) => {
    if (query === null) return;
    onInsert(insertSuggestion(text, query, caret, suggestion), suggestion);
  };

  const handleKey = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (query === null || suggestions.length === 0 || event.nativeEvent.isComposing) return false;
    const next = nextSuggestionIndex(event.key, active, suggestions.length);
    const picked = suggestions[active];
    if (next !== null) {
      setActiveIndex(next);
    } else if ((event.key === "Enter" || event.key === "Tab") && !event.shiftKey && picked !== undefined) {
      choose(picked);
    } else if (event.key === "Escape") {
      setDismissedStart(query.start);
    } else {
      return false;
    }
    event.preventDefault();
    return true;
  };

  return { suggestions, activeIndex: active, someFailed: query !== null && sources.someFailed, choose, handleKey };
}
